import React, { useEffect, useState, FunctionComponent } from 'react';
import Text from 'common/components/Text/Text';

import styles from './Applications.module.scss';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useMediaQuery } from 'react-responsive';
import {
  useGetPostulationsQuery,
  useGetBackofficeUserPostulationsQuery,
} from 'services/exploreApi';
import { Program, Postulation } from 'app/types';
import FavoriteCard from 'features/user/dashboard/pages/shorlist/FavoriteCard';
import Skeletons from 'common/components/Skeletons/Skeletons';
import PostulationModal from 'features/backoffice/Postulation/Modal';

interface CardProps {
  program: Program;
  index: number;
  postulation: Postulation;
}

const Card = ({ program, index, postulation }: CardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  return (
    <div className={styles.applyCard}>
      <Text
        size={isMobile ? 14 : 16}
        weight={600}
        inlineStyle={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          paddingRight: '10px',
        }}
      >
        {program.name}
      </Text>
      <Text
        size={14}
        weight={400}
        inlineStyle={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          paddingRight: '10px',
        }}
      >
        {program.school_name}
      </Text>
      {!isMobile && (
        <div className={`${styles.number}`}>
          <Text size={14} weight={400}>
            {index + 1}
          </Text>
        </div>
      )}
      <div className={`${styles.container}`}>
        <i className="bi bi-hourglass-top"></i>
        <div className={`${styles.status} ${styles[postulation.status]}`}>
          {postulation.status.replace('_', ' ')}
        </div>
        {postulation.intake_date &&
          postulation.intake_date.next_deadline !== null &&
          program.study_options[0].intake_dates?.[0].next_deadline !== null && (
            <i className="bi bi-alarm"></i>
          )}
        <Text size={14} weight={500}>
          {postulation.intake_date && postulation.intake_date.next_deadline !== null
            ? `${postulation.intake_date.next_deadline}`
            : program.study_options[0].intake_dates?.[0].next_deadline !== null
            ? `${program.study_options[0].intake_dates?.[0].next_deadline}`
            : ''}
        </Text>
      </div>
      <div className={`${styles.progress} progress`}>
        <div
          className={`${styles.progressBar} progress-bar`}
          role="progressbar"
          style={{ width: `${postulation.progress}%` }}
          aria-valuenow={25}
          aria-valuemin={0}
          aria-valuemax={100}
        ></div>
      </div>
      {!isMobile && <div className={`${styles.progressNumber}`}>{`${postulation.progress}%`}</div>}
      <button
        className={`${styles.button} btn`}
        onClick={() => {
          navigate(`applications/${postulation.id}`);
        }}
      >
        <Text size={isMobile ? 14 : 16} weight={700}>
          {postulation.status === 'accepted' ? 'Go to enroll' : 'Enter Application'}
        </Text>
      </button>
    </div>
  );
};
const Applications: FunctionComponent = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const location = useLocation();
  let isLoading: boolean;
  let postulationsList: any;
  const [showModal, setShowModal] = useState<boolean>(false);
  const isBackoffice = location.pathname.includes('backoffice');
  if (isBackoffice) {
    const { id } = useParams<{ id: string }>();
    ({ data: postulationsList, isLoading } = useGetBackofficeUserPostulationsQuery(id || '1'));
  } else {
    ({ data: postulationsList, isLoading } = useGetPostulationsQuery());
  }
  const [filterData, setFilterData] = useState<Postulation[]>([]);
  const [filter, setFilter] = useState<string>('');
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(event.target.value);
  };
  useEffect(() => {
    if (postulationsList) {
      if (filter === '') {
        setFilterData([...postulationsList.data.postulations]);
      } else {
        setFilterData(
          postulationsList?.data.postulations.filter(
            (postulation: Postulation) => postulation.status === filter
          )
        );
      }
    }
  }, [filter, postulationsList]);
  return (
    <div className={styles.applicationsContainer}>
      <div className={!isMobile ? `${styles.header}` : 'none'}>
        <div className={`${styles.headerWrap}`}>
          <h1 className={styles.title}>Application List</h1>
          {/* <Text size={isMobile ? 20 : 36} weight={700}>
            Application List
          </Text> */}
          {!isMobile && isBackoffice && (
            <button className={`${styles.addButton} btn`} onClick={() => setShowModal(true)}>
              <i className="bi bi-plus-lg" />
            </button>
          )}
          {!isMobile && isBackoffice && (
            <PostulationModal show={showModal} setShow={setShowModal} />
          )}
        </div>
        {!isMobile && (
          <select className={`${styles.filter}`} onChange={handleSelectChange}>
            <option value={''}> All </option>
            <option value={'pending'}> Pending </option>
            <option value={'preparing'}> Preparing </option>
          </select>
        )}
      </div>
      {isLoading ? (
        <Skeletons />
      ) : (
        <div className={`row flex-row mt-3 pt-1`}>
          {filterData?.map((postulation: Postulation, index: number) => (
            <Card
              program={postulation.program}
              postulation={postulation}
              index={index}
              key={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Applications;
